import { type Ref, forwardRef, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { type CountryCode, type CountryCallingCode } from 'libphonenumber-js';

import { DataProvider } from '@hh.ru/magritte-common-data-provider';
import { useFlagEmojiFont, useIsFlagEmojiSupported } from '@hh.ru/magritte-common-flag-emoji-font';
import { Cell, CellRightLabel, CellText } from '@hh.ru/magritte-ui-cell';
import { CallingCodeInput } from '@hh.ru/magritte-ui-phone-input/CallingCodeInput';
import { type PhoneCodeOption } from '@hh.ru/magritte-ui-phone-input/useCountrySelectDataProvider';
import { type LibPhoneNumberAPI, type MetadataVariant } from '@hh.ru/magritte-ui-phone-input/useLibPhoneNumber';
import { RenderOptionType, Select } from '@hh.ru/magritte-ui-select';

import styles from './phoneInput.less';

const getFlagEmoji = (countryCode: CountryCode) => {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};

const renderSelectItem: RenderOptionType<PhoneCodeOption> = ({ data, onChange, type }) => {
    if (type === 'delimiter') {
        return (
            <CellText maxLines={1} style="secondary">
                {data.text}
            </CellText>
        );
    }

    return (
        <Cell
            left={
                <div className={styles.flagContainer}>
                    <span className={classNames(styles.flagEmoji)}>{getFlagEmoji(data.value)}</span>
                </div>
            }
            right={<CellRightLabel hideIcon>+{data.callingCode}</CellRightLabel>}
            horPadding={false}
            vertPadding={false}
            onClick={() => onChange(data, true)}
        >
            <CellText>{data.text}</CellText>
        </Cell>
    );
};

type RegionSelectProps = {
    country: CountryCode;
    selectDataProvider: DataProvider<PhoneCodeOption>;
    selectOptions: PhoneCodeOption[];
    callingCode: CountryCallingCode;
    onChange: (country: CountryCode, callingCode: CountryCallingCode) => void;
    availableCountries?: CountryCode[];
    priorityCountries: CountryCode[];
    invalid?: boolean;
    libPhoneNumberAPI: LibPhoneNumberAPI;
    searchable: boolean;
    disabled?: boolean;
    metadataVariant: MetadataVariant;
};

export const RegionSelect = forwardRef<HTMLInputElement, RegionSelectProps>(
    (
        {
            selectDataProvider,
            selectOptions,
            country,
            callingCode,
            onChange,
            invalid,
            libPhoneNumberAPI,
            searchable,
            disabled,
            metadataVariant,
        },
        ref
    ) => {
        const { getCountryCallingCode } = libPhoneNumberAPI;
        const [inputValue, setInputValue] = useState<string>(`+${callingCode}`);
        const exactMatchRef = useRef(true);

        const isFlagEmojiSupported = useIsFlagEmojiSupported();
        useFlagEmojiFont(metadataVariant, isFlagEmojiSupported);
        const pickerRefCallback = useCallback(
            (node: HTMLElement) => {
                if (node) {
                    node.classList.toggle(styles.noFlagEmoji, !isFlagEmojiSupported);
                }
            },
            [isFlagEmojiSupported]
        );

        useEffect(() => {
            setInputValue(`+${callingCode}`);
        }, [callingCode]);

        const value = useMemo(() => selectOptions.find((item) => item.value === country), [selectOptions, country]);
        return (
            <Select
                type="radio"
                dataProvider={selectDataProvider}
                value={value}
                onChange={(selectedValue) => {
                    exactMatchRef.current = true;
                    onChange?.(selectedValue.value, getCountryCallingCode(selectedValue.value));
                }}
                triggerProps={{ size: 'large' }}
                widthEqualToActivator={false}
                searchable={searchable}
                renderMobileItem={renderSelectItem}
                renderDesktopItem={renderSelectItem}
                pickerRef={pickerRefCallback}
                renderTrigger={({ setSearchValue, onFocus, onBlur, onKeyDown, ref: triggerRef }) => (
                    <CallingCodeInput
                        ref={triggerRef as Ref<HTMLDivElement>}
                        inputElementRef={ref}
                        className={!isFlagEmojiSupported && styles.noFlagEmoji}
                        flag={<span className={classNames(styles.flagEmoji)}>{getFlagEmoji(country)}</span>}
                        invalid={invalid}
                        disabled={disabled}
                        onChange={(value) => {
                            if (value === '') {
                                setInputValue('+');
                                setSearchValue('');
                            }
                            if (!value.startsWith('+')) {
                                return;
                            }
                            const code = value.substring(1);
                            let hasPartialMatch = false;
                            const exactMatch = selectOptions.find((option) => {
                                if (!option.value) {
                                    return false;
                                }

                                if (option.callingCode.startsWith(code)) {
                                    hasPartialMatch = true;
                                }

                                return option.callingCode === code;
                            });

                            // выполнится даже если есть точное совпадение
                            if (hasPartialMatch) {
                                exactMatchRef.current = false;
                                setInputValue(`+${code}`);
                                setSearchValue(code === '' ? '' : `+${code}`);
                            }

                            if (exactMatch) {
                                exactMatchRef.current = true;
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                onChange(exactMatch.value, exactMatch.callingCode);
                            }
                        }}
                        value={inputValue}
                        onFocus={() => {
                            setSearchValue('');
                            onFocus?.();
                        }}
                        onBlur={() => {
                            if (!exactMatchRef.current) {
                                const code = inputValue.substring(1);
                                const bestMatch = selectOptions.find((option) => option?.callingCode.startsWith(code));
                                if (bestMatch && bestMatch.value) {
                                    onChange(bestMatch.value, bestMatch.callingCode);
                                    setInputValue(`+${bestMatch.callingCode}`);
                                }
                            }
                            onBlur?.();
                        }}
                        onKeyDown={onKeyDown}
                    />
                )}
            />
        );
    }
);

RegionSelect.displayName = 'RegionSelect';
